import React, { useState } from 'react'
import type { QuoteFile } from 'paintscout'
import type { PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'
import { usePresentation } from '../../../context'
import { Grid, useMediaQuery, useInView } from '@paintscout/ui'
import { Carousel, ViewMediaDialog } from '../../components'
import { InlineEditableMedia } from '../../inlineEditable'

import PresentationFilesGrid from '../../components/PresentationFilesGrid'
import { FileDropArea, useDialogs } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { appendGalleryFiles } from './galleryFiles'
import { addNewFilesToLibrary } from '../../util/addNewFilesToLibrary'
import { getUploadedFiles } from '../../util/uploadedFiles'

/**
 * 'auto' picks a column count (capped at 4, matching the fixed column widths
 * PresentationFilesGrid already supports) that divides evenly into the file count, so the last
 * row isn't left with a single orphaned image — falling back to 4 for counts with no clean
 * divisor (e.g. 5 or 7 files).
 */
function pickAutoColumns(count: number): number {
  // no files yet (e.g. a freshly-added, still-empty gallery) — match the standard 3-column
  // default rather than picking a 1-wide layout for whatever empty-state UI renders
  if (!count) return 3
  if (count <= 4) return count
  for (const divisor of [4, 3, 2]) {
    if (count % divisor === 0) return divisor
  }
  return 4
}

function GalleryBlock(props: PresentationBlockProps) {
  const { openDialog, dismissDialog } = useDialogs()
  const {
    isEditable,
    isPreview,
    isSettings,
    presentationIndustry,
    block,
    blockPrefix,
    setFieldValue,
    submitForm,
    WrapperComponent
  } = props
  const { fields } = block
  const { files, columns: fieldsColumns, showBorder } = fields
  const columns = fieldsColumns === 'auto' ? pickAutoColumns(files?.length || 0) : fieldsColumns
  const { setIsDirty, focusedBlock, setFocusedBlock } = usePresentation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '-200px 0px'
  })
  const [isSwitching, setIsSwitching] = useState(false)
  const [index, setIndex] = useState(0)
  const isMobile = useMediaQuery('xs')
  const { presentationLibrary } = usePresentation()
  const { s3MediaUpload } = useFlags()
  // Files dropped from Finder onto the block go straight in; in Preview there's nothing to edit.
  const canDropFiles = isSettings && !isPreview

  const handleSwitching = () => {
    if (!isSwitching) setIsSwitching(true)
  }

  const handleTransitionEnd = () => {
    setIsSwitching(false)
  }

  const handleClick = (index: number) => {
    return isSettings || isSwitching ? null : handleViewFiles(index)
  }

  const filesGrid = (
    <PresentationFilesGrid
      mediaOptionsProps={{
        blockPrefix: blockPrefix
      }}
      isEditable={isEditable}
      isSettings={isSettings}
      inPresentation={true}
      onAddFiles={isSettings ? handleAddFiles : undefined}
      onDelete={isEditable ? handleDeleteFile : null}
      editable={isEditable}
      onReorder={handleReorder}
      justify="space-around"
      files={files}
      columns={columns}
      showBorder={showBorder}
      presentationIndustry={presentationIndustry}
      presentationLibrary={presentationLibrary}
      showDetails={false}
      onFileClick={isSettings ? null : handleViewFiles}
      handleEditFile={handleEditFile}
      fullItemHeight={true}
      filePreviewProps={{ showFirstPageOnly: true }}
    />
  )

  if (!isEditable && !isSettings && isMobile) {
    return (
      <WrapperComponent {...props}>
        <div ref={ref}>
          <Carousel handleSwitching={handleSwitching} handleTransitionEnd={handleTransitionEnd} index={index}>
            {files.map((file, index) => (
              <div key={file?.src || index} onClick={() => handleClick(index)}>
                <InlineEditableMedia
                  file={file?.src ? file : null}
                  handleEdit={() => null}
                  isAnimated={inView}
                  showAllPages
                  isEditable={false}
                  isSettings={false}
                  presentationIndustry={presentationIndustry}
                  presentationLibrary={presentationLibrary}
                  filePreviewProps={{ showFirstPageOnly: true }}
                />
              </div>
            ))}
          </Carousel>
        </div>
      </WrapperComponent>
    )
  } else
    return (
      <WrapperComponent {...props}>
        <Grid container spacing={3} justifyContent={'center'} alignItems={'flex-start'}>
          <Grid item xs={12}>
            {canDropFiles ? (
              // Dropping several files from Finder anywhere on the block uploads them all and
              // appends them (#23709) — the library dialog uses this same drop area.
              <FileDropArea onUpload={handleDropFiles} s3MediaUploadEnabled={s3MediaUpload} multiple>
                {filesGrid}
              </FileDropArea>
            ) : (
              filesGrid
            )}
          </Grid>
        </Grid>
      </WrapperComponent>
    )

  function handleDeleteFile(index: number) {
    const newFiles = [...files]
    newFiles.splice(index, 1)
    setFieldValue(`${blockPrefix}.fields.files`, newFiles)
    setIsDirty(true)
  }

  function handleEditFile(field: string, content: any) {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  function handleReorder(reorderedFiles: QuoteFile[]) {
    if (focusedBlock !== block.key) setFocusedBlock(block.key)
    setFieldValue(`${blockPrefix}.fields.files`, reorderedFiles)
    setIsDirty(true)
  }

  /**
   * Appends every file a picker returned — the multi-select Library dialog, or a tile's own picker
   * handing over everything past the file it kept (#23709). Writes the whole array like
   * handleReorder does, not the indexed path handleEditFile uses.
   */
  function handleAddFiles(newFiles: QuoteFile[]) {
    const updated = appendGalleryFiles(files, newFiles)
    if (updated === files) return
    if (focusedBlock !== block.key) setFocusedBlock(block.key)
    setFieldValue(`${blockPrefix}.fields.files`, updated)
    setIsDirty(true)
  }

  /**
   * Files dragged from Finder onto the block. They never went through the Library dialog, so they
   * are auto-added to the library here under the same rule InlineEditableMedia.handleChange uses
   * for its own direct uploads.
   */
  function handleDropFiles(uploaded: QuoteFile[]) {
    addNewFilesToLibrary({ files: getUploadedFiles(uploaded), presentationLibrary })
    handleAddFiles(uploaded)
  }

  function handleViewFiles(index: number) {
    openDialog(ViewMediaDialog, {
      files: files.filter((file) => !!file?.src),
      index,
      onIndexChange: setIndex,
      enableDownload: !isSettings,
      onClose: dismissDialog
    })
  }
}

export default React.memo(GalleryBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
